import { useCallback, Fragment } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Pencil } from "lucide-react";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  QuoteLine,
  QuoteItemLine,
  QuoteSectionLine,
  QuoteNoteLine,
  HydratedQuoteLine,
  HydratedQuoteItemLine,
  HydratedQuantityBreak,
  Item,
  HydratedItem,
  ItemReviewStatus,
  type CurrencyCode,
  computeLineNumbers,
} from "@forgepoint/shared";
import { useItemsStore } from "@/modules/items/itemsStore";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { useFeatureFlag } from "@/store/featureFlagStore";
import type { QuoteCurrencyFields } from "@/lib/quoteCurrency";
import { ItemParentRow } from "./rows/ItemParentRow";
import { ItemQuantityRow } from "./rows/ItemQuantityRow";
import { SectionRow } from "./rows/SectionRow";
import { NoteRow } from "./rows/NoteRow";

interface QuoteItemsTableProps {
  lines: QuoteLine[];
  hydratedLines?: HydratedQuoteLine[];
  readOnly: boolean;
  currencyCode?: CurrencyCode;
  currencyContext?: QuoteCurrencyFields;
  currencyContextDirty?: boolean;
  onReorder: (oldIndex: number, newIndex: number) => void;
  onUpdateLine: (index: number, line: QuoteLine) => void;
  onRemoveLine: (index: number) => void;
  onOpenBOM: (index: number, item: Item, quantities: string[]) => void;
}

/**
 * Editable table of a quote's lines that offers add-quantity only while a line is under the org's quantity break limit.
 * @param props - The lines, currency context, and line change handlers
 * @returns The quote lines table
 */
export function QuoteItemsTable({
  lines,
  hydratedLines,
  readOnly,
  currencyCode,
  currencyContext,
  currencyContextDirty,
  onReorder,
  onUpdateLine,
  onRemoveLine,
  onOpenBOM,
}: QuoteItemsTableProps) {
  const { items, hydratedItems, markItemsReviewed } = useItemsStore();
  const showMargin = useFeatureFlag("quote-line-margin");
  const allowLineUnitOverride = useQuoteSettingsStore(
    (state) => state.settings?.allowLineUnitOverride ?? false,
  );
  const maxQuantityBreaksPerLine = useQuoteSettingsStore(
    (state) => state.settings?.maxQuantityBreaksPerLine ?? null,
  );
  const showUnitPicker = allowLineUnitOverride && !readOnly;
  const unitColumnWidth = showUnitPicker ? "w-14 sm:w-20" : "w-10 sm:w-16";

  const getHydratedLine = useCallback(
    (lineId: string) => {
      const found = hydratedLines?.find((line) => line.id === lineId);
      if (found && found.type === "ITEM") {
        return found;
      }
      return undefined;
    },
    [hydratedLines],
  );

  // Filter to only ITEM, SECTION, and NOTE lines (not DISCOUNT)
  const nonDiscountLines = lines.filter((l) => l.type !== "DISCOUNT");

  // Compute sequential line numbers: only ITEM lines get numbers
  const lineNumbers = computeLineNumbers(lines);
  const lineNumberMap = new Map<string, number>();
  lines.forEach((line, index) => {
    const num = lineNumbers[index];
    if (num !== null) {
      lineNumberMap.set(line.id, num);
    }
  });

  // Create sortable IDs using line.id for stable identity
  const sortableIds = nonDiscountLines.map((line) => line.id);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = sortableIds.indexOf(active.id as string);
    const newIndex = sortableIds.indexOf(over.id as string);

    if (oldIndex !== -1 && newIndex !== -1) {
      onReorder(oldIndex, newIndex);
    }
  };

  // Handlers for ItemQuantityRow
  const handleQuantityChange = useCallback(
    (
      lineIndex: number,
      line: QuoteItemLine,
      qtyIndex: number,
      value: string,
    ) => {
      const newBreaks = [...line.quantityBreaks];
      newBreaks[qtyIndex] = { ...newBreaks[qtyIndex], quantity: value };
      onUpdateLine(lineIndex, { ...line, quantityBreaks: newBreaks });
    },
    [onUpdateLine],
  );

  const handleAddQuantity = useCallback(
    (lineIndex: number, line: QuoteItemLine) => {
      const lastBreak = line.quantityBreaks[line.quantityBreaks.length - 1];
      const lastQtyNum = parseInt(lastBreak.quantity, 10);
      const nextQty = isNaN(lastQtyNum) ? "1" : String(lastQtyNum + 1);
      const newBreaks = [...line.quantityBreaks, { quantity: nextQty }];
      onUpdateLine(lineIndex, { ...line, quantityBreaks: newBreaks });
    },
    [onUpdateLine],
  );

  /**
   * Builds the add-quantity handler for a line, or none once the line holds the most quantity breaks allowed.
   * @param lineIndex - The line's position in the quote
   * @param line - The item line
   * @returns The add-quantity handler, or undefined when the line is at its limit
   */
  const getAddQuantityHandler = (
    lineIndex: number,
    line: QuoteItemLine,
  ): (() => void) | undefined => {
    if (
      maxQuantityBreaksPerLine !== null &&
      line.quantityBreaks.length >= maxQuantityBreaksPerLine
    ) {
      return undefined;
    }
    return () => handleAddQuantity(lineIndex, line);
  };

  const handleRemoveQuantity = useCallback(
    (lineIndex: number, line: QuoteItemLine, qtyIndex: number) => {
      if (line.quantityBreaks.length > 1) {
        const newBreaks = line.quantityBreaks.filter((_, i) => i !== qtyIndex);
        onUpdateLine(lineIndex, { ...line, quantityBreaks: newBreaks });
      }
    },
    [onUpdateLine],
  );

  const handleSetOverride = useCallback(
    (
      lineIndex: number,
      line: QuoteItemLine,
      qtyIndex: number,
      currentPrice: string,
    ) => {
      const newBreaks = [...line.quantityBreaks];
      newBreaks[qtyIndex] = {
        ...newBreaks[qtyIndex],
        unitPriceOverride: currentPrice,
      };
      onUpdateLine(lineIndex, { ...line, quantityBreaks: newBreaks });
    },
    [onUpdateLine],
  );

  const handleOverrideChange = useCallback(
    (
      lineIndex: number,
      line: QuoteItemLine,
      qtyIndex: number,
      value: string,
    ) => {
      const newBreaks = [...line.quantityBreaks];
      newBreaks[qtyIndex] = {
        ...newBreaks[qtyIndex],
        unitPriceOverride: value,
      };
      onUpdateLine(lineIndex, { ...line, quantityBreaks: newBreaks });
    },
    [onUpdateLine],
  );

  const handleClearOverride = useCallback(
    (lineIndex: number, line: QuoteItemLine, qtyIndex: number) => {
      const newBreaks = [...line.quantityBreaks];
      const hydratedBreak = getHydratedLine(line.id)?.quantityBreaks?.[
        qtyIndex
      ];
      newBreaks[qtyIndex] = restoreQuantityBreak(
        line.quantityBreaks[qtyIndex],
        hydratedBreak,
      );
      onUpdateLine(lineIndex, { ...line, quantityBreaks: newBreaks });
    },
    [getHydratedLine, onUpdateLine],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className="@container/quote-items">
        <Table className="w-full">
          <colgroup>
            <col className="w-6 sm:w-10" /> {/* Drag handle */}
            <col className="w-6 sm:w-8" /> {/* Line # */}
            <col className="w-48 sm:w-96" /> {/* Item Details */}
            <col className="w-16 sm:w-28" /> {/* Qty */}
            <col className={unitColumnWidth} /> {/* Unit */}
            {showMargin && <col className="w-14 sm:w-20" />} {/* Margin */}
            <col className="w-32 sm:w-56" /> {/* Unit Price */}
            <col className="w-16 sm:w-28" /> {/* Total */}
            <col className="w-8 sm:w-20" /> {/* Actions */}
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead />
              <TableHead className="text-xs sm:text-sm text-muted-foreground">
                #
              </TableHead>
              <TableHead className="text-xs sm:text-sm">
                <span className="inline-flex items-center gap-1">
                  {!readOnly && (
                    <Pencil
                      aria-hidden="true"
                      className="size-[11px] shrink-0 opacity-50"
                    />
                  )}
                  Item Details
                </span>
              </TableHead>
              <TableHead className="text-right text-xs sm:text-sm">
                <span className="inline-flex items-center gap-1">
                  {!readOnly && (
                    <Pencil
                      aria-hidden="true"
                      className="size-[11px] shrink-0 opacity-50"
                    />
                  )}
                  Qty
                </span>
              </TableHead>
              <TableHead className="text-center text-xs sm:text-sm">
                <span className="inline-flex items-center gap-1">
                  {showUnitPicker && (
                    <Pencil
                      aria-hidden="true"
                      className="size-[11px] shrink-0 opacity-50"
                    />
                  )}
                  Unit
                </span>
              </TableHead>
              {showMargin && (
                <TableHead className="text-right text-xs sm:text-sm">
                  Margin
                </TableHead>
              )}
              <TableHead className="text-right text-xs sm:text-sm">
                <span className="inline-flex items-center gap-1">
                  {!readOnly && (
                    <Pencil
                      aria-hidden="true"
                      className="size-[11px] shrink-0 opacity-50"
                    />
                  )}
                  Price
                </span>
              </TableHead>
              <TableHead className="text-right text-xs sm:text-sm">
                Total
              </TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {nonDiscountLines.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={showMargin ? 9 : 8}
                  className="text-center py-8 text-muted-foreground"
                >
                  No line items yet. Add an item or section to get started.
                </TableCell>
              </TableRow>
            ) : (
              <SortableContext
                items={sortableIds}
                strategy={verticalListSortingStrategy}
              >
                {nonDiscountLines.map((line, index) => {
                  const sortableId = line.id;

                  if (line.type === "SECTION") {
                    return (
                      <SectionRow
                        key={sortableId}
                        id={sortableId}
                        lineNumber={lineNumberMap.get(line.id) ?? null}
                        line={line as QuoteSectionLine}
                        readOnly={readOnly}
                        onUpdate={(updated) => onUpdateLine(index, updated)}
                        onRemove={() => onRemoveLine(index)}
                      />
                    );
                  }

                  if (line.type === "NOTE") {
                    return (
                      <NoteRow
                        key={sortableId}
                        id={sortableId}
                        lineNumber={lineNumberMap.get(line.id) ?? null}
                        line={line as QuoteNoteLine}
                        readOnly={readOnly}
                        onUpdate={(updated) => onUpdateLine(index, updated)}
                        onRemove={() => onRemoveLine(index)}
                      />
                    );
                  }

                  if (line.type === "ITEM") {
                    const itemLine = line as QuoteItemLine;
                    const selectedItem = itemLine.itemId
                      ? (items.get(itemLine.itemId) ?? null)
                      : null;
                    const hydratedLine = getHydratedLine(itemLine.id);
                    const hasMultipleQuantities =
                      itemLine.quantityBreaks.length > 1;
                    const reviewStatus = getItemReviewStatus(
                      itemLine.itemId,
                      items,
                      hydratedItems,
                      hydratedLine,
                    );
                    const isAssembly =
                      hydratedLine?.item?.type === "MANUFACTURED" &&
                      (hydratedLine.item.components?.length ?? 0) > 0;

                    return (
                      <Fragment key={sortableId}>
                        <ItemParentRow
                          key={sortableId}
                          id={sortableId}
                          lineNumber={lineNumberMap.get(line.id) ?? null}
                          line={itemLine}
                          selectedItem={selectedItem}
                          hydratedLine={hydratedLine}
                          readOnly={readOnly}
                          hasMultipleQuantities={hasMultipleQuantities}
                          reviewStatus={reviewStatus}
                          currencyCode={currencyCode}
                          currencyContext={currencyContext}
                          currencyContextDirty={currencyContextDirty}
                          onMarkReviewed={
                            reviewStatus === "NEEDS_REVIEW" &&
                            itemLine.itemId &&
                            !isAssembly
                              ? () => markItemsReviewed([itemLine.itemId!])
                              : undefined
                          }
                          onUpdate={(updated) => onUpdateLine(index, updated)}
                          onRemove={() => onRemoveLine(index)}
                          onOpenBOM={() => {
                            if (selectedItem) {
                              onOpenBOM(
                                index,
                                selectedItem,
                                itemLine.quantityBreaks.map(
                                  (qb) => qb.quantity,
                                ),
                              );
                            }
                          }}
                          onQuantityChange={(v) =>
                            handleQuantityChange(index, itemLine, 0, v)
                          }
                          onAddQuantity={getAddQuantityHandler(index, itemLine)}
                          onSetOverride={(price) =>
                            handleSetOverride(index, itemLine, 0, price)
                          }
                          onOverrideChange={(v) =>
                            handleOverrideChange(index, itemLine, 0, v)
                          }
                          onClearOverride={() =>
                            handleClearOverride(index, itemLine, 0)
                          }
                        />
                        {/* Only render quantity rows for quantities after the first */}
                        {itemLine.quantityBreaks
                          .slice(1)
                          .map((_, sliceIndex) => {
                            const qtyIndex = sliceIndex + 1;
                            const isLastQty =
                              qtyIndex === itemLine.quantityBreaks.length - 1;
                            return (
                              <ItemQuantityRow
                                key={`${sortableId}-qty-${qtyIndex}`}
                                line={itemLine}
                                selectedItem={selectedItem}
                                hydratedLine={hydratedLine}
                                quantityIndex={qtyIndex}
                                readOnly={readOnly}
                                isLast={isLastQty}
                                isLastInGroup={isLastQty}
                                canRemove={true}
                                currencyCode={currencyCode}
                                currencyContext={currencyContext}
                                currencyContextDirty={currencyContextDirty}
                                onQuantityChange={(qi, v) =>
                                  handleQuantityChange(index, itemLine, qi, v)
                                }
                                onAddQuantity={getAddQuantityHandler(
                                  index,
                                  itemLine,
                                )}
                                onRemoveQuantity={(qi) =>
                                  handleRemoveQuantity(index, itemLine, qi)
                                }
                                onSetOverride={(qi, price) =>
                                  handleSetOverride(index, itemLine, qi, price)
                                }
                                onOverrideChange={(qi, v) =>
                                  handleOverrideChange(index, itemLine, qi, v)
                                }
                                onClearOverride={(qi) =>
                                  handleClearOverride(index, itemLine, qi)
                                }
                              />
                            );
                          })}
                      </Fragment>
                    );
                  }

                  return null;
                })}
              </SortableContext>
            )}
          </TableBody>
        </Table>
      </div>
    </DndContext>
  );
}

function restoreQuantityBreak(
  quantityBreak: QuoteItemLine["quantityBreaks"][number],
  _hydratedBreak: HydratedQuantityBreak | undefined,
): QuoteItemLine["quantityBreaks"][number] {
  return {
    quantity: quantityBreak.quantity,
  };
}

/**
 * Gets the review status for a quote line item, preferring fresh data from the
 * items store (which is optimistically updated) over the quote's hydrated snapshot.
 * For assemblies, walks the hydrated item tree from the store.
 */
function getItemReviewStatus(
  itemId: string | null,
  itemsMap: Map<string, Item>,
  hydratedItemsMap: Map<string, HydratedItem>,
  hydratedLine: HydratedQuoteItemLine | undefined,
): ItemReviewStatus | null {
  if (!itemId) {
    return null;
  }

  // Prefer hydrated item from store (deep-updated on mark-reviewed)
  const hydratedItem = hydratedItemsMap.get(itemId);
  if (hydratedItem) {
    return computeAssemblyReviewStatus(hydratedItem);
  }

  // Fall back to flat item from store
  const storeItem = itemsMap.get(itemId);
  if (storeItem) {
    return storeItem.reviewStatus ?? null;
  }

  // Last resort: quote's hydrated snapshot
  if (hydratedLine?.item) {
    return computeAssemblyReviewStatus(hydratedLine.item);
  }

  return null;
}

/**
 * Recursively walks the component tree of an item.
 * Returns NEEDS_REVIEW if any node (including the root) needs review.
 */
function computeAssemblyReviewStatus(
  item: HydratedItem,
): ItemReviewStatus | null {
  if (item.reviewStatus === "NEEDS_REVIEW") {
    return "NEEDS_REVIEW";
  }

  const hasComponentNeedingReview = item.components?.some(
    (comp) => computeAssemblyReviewStatus(comp.component) === "NEEDS_REVIEW",
  );
  if (hasComponentNeedingReview) {
    return "NEEDS_REVIEW";
  }

  if (item.reviewStatus === "REVIEWED") {
    return "REVIEWED";
  }

  const hasAnyReviewedComponent = item.components?.some(
    (comp) => computeAssemblyReviewStatus(comp.component) === "REVIEWED",
  );
  if (hasAnyReviewedComponent) {
    return "REVIEWED";
  }

  return item.reviewStatus ?? null;
}
